import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TitleService } from 'src/app/services/title.service';
import { Application, CombineApplicationsView, ConnectionRequest, ExternalApiDetails } from '../../store';
import { ApiEcoState } from '../../store/api-eco.state';

@Component({
  selector: 'kp-api-subscribe-page',
  templateUrl: './api-subscribe-page.component.html',
  styleUrls: ['./api-subscribe-page.component.scss'],
})
export class ApiSubscribePageComponent implements OnInit, OnDestroy {
  selectedApiDetails: ExternalApiDetails;
  applicationsData: Application[];
  private destroyed = new Subject();

  constructor(
    private store: Store,
    private titleService: TitleService
  ) {}

  ngOnInit(): void {
    this.titleService.setDocTitle('Add API: Select Applications to Use API | Kaiser Permanente Developer Portal');
    combineLatest([
      this.store.select(ApiEcoState.getSelectedApiDetail),
      this.store.select(ApiEcoState.getExternalApplications),
    ])
      .pipe(takeUntil(this.destroyed))
      .subscribe(([selectedAPI, applications]: [ExternalApiDetails, CombineApplicationsView]) => {
        this.handleApplicationsData(selectedAPI, applications);
      });
  }

  private handleApplicationsData(selectedAPI: ExternalApiDetails, applications: CombineApplicationsView): void {
    this.selectedApiDetails = selectedAPI;
    this.applicationsData = [];

    this.processApplications(applications.personalApplications, selectedAPI);
    if (applications?.groupApplications?.length) {
      this.processApplications(applications.groupApplications, selectedAPI, true);
    }
  }

  private processApplications(apps: Application[], selectedAPI: ExternalApiDetails, isGroup: boolean = false): void {
    apps.forEach((app: Application) => {
      const processedApp = this.createProcessedApplication(app, selectedAPI, isGroup);
      this.applicationsData.push(processedApp);
    });
  }

  private createProcessedApplication(app: Application, selectedAPI: ExternalApiDetails, isGroup: boolean): Application {
    const hasRevoked = this.hasRevokedConnection(app.connectionRequestList);
    const isSubscribed = this.isSubscribedToApi(app.connectionRequestList, selectedAPI.id);
    
    const processedApp: Application = {
      ...app,
      hasRevoked,
      isSubscribed,
    };
    
    if (isGroup) {
      processedApp.name += ' (Group Application)';
    }
    
    return processedApp;
  }

  private hasRevokedConnection(connectionRequests: ConnectionRequest[]): boolean {
    return !!connectionRequests.find(
      (connection: ConnectionRequest) => connection.status.toLowerCase() === 'revoked'
    );
  }

  private isSubscribedToApi(connectionRequests: ConnectionRequest[], apiId: string): boolean {
    return !!connectionRequests?.find(
      (connection: ConnectionRequest) => connection.serviceId === apiId
    );
  }

  ngOnDestroy(): void {
    this.titleService.setDocTitle('api-ecosystem');
  }
}
